import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { Checkbox } from 'app/shared/input/checkbox';
import { MultiSelect } from 'app/shared/select';
import { useLeadOrganisationService } from 'hooks/api/agents/lead/orgnisation';
import { useLeadContactService } from 'hooks/api/agents/lead/contact';
import { addManyLeadToStore } from 'store/actions/lead';
import styles from './create.module.css';

const INDUSTRY_OPTIONS = [
  'Accounting',
  'Advertising',
  'Agriculture',
  'Automotive',
  'Banking',
  'Construction',
  'Consulting',
  'Ecommerce',
  'Education',
  'Energy',
  'Entertainment',
  'Fashion',
  'Finance',
  'Food & Beverage',
  'Government',
  'Healthcare',
  'Hospitality',
  'Insurance',
  'Legal',
  'Logistics',
  'Manufacturing',
  'Marketing',
  'Media',
  'Non-profit',
  'Real Estate',
  'Retail',
  'SaaS',
  'Software',
  'Technology',
  'Telecommunications',
  'Transportation',
  'Travel',
  'Other'
].map((label) => ({ label, value: label.toLowerCase() }));

const HEADCOUNT_OPTIONS = [
  { label: '1–10', value: 10 },
  { label: '11–50', value: 50 },
  { label: '51–200', value: 200 },
  { label: '201–500', value: 500 },
  { label: '501–1,000', value: 1000 },
  { label: '1,001–5,000', value: 5000 },
  { label: '5,000+', value: 10000 }
];

const ESTIMATED_CUSTOMERS_OPTIONS = [
  { label: '0–2,500', value: 2500 },
  { label: '2,500–5,000', value: 5000 },
  { label: '5,000–10,000', value: 10000 },
  { label: '10,000–25,000', value: 25000 },
  { label: '25,000–50,000', value: 50000 },
  { label: '50,000–100,000', value: 100000 },
  { label: '100,000+', value: 250000 }
];

const INITIAL_FORM = {
  name: '',
  website: '',
  industry: '',
  headcount: '',
  estimated_customers: '',
  score: '',
  has_web_app: false,
  has_mobile_app: false,
  has_newsletter: false
};

const EMPTY_CONTACT = {
  first_name: '',
  last_name: '',
  title: '',
  email: '',
  phone: ''
};

const toNumber = (value) => (value === '' || value == null ? null : Number(value));

export const CreateLeadForm = ({ status_slug = null, closeModal = () => {} }) => {
  const dispatch = useDispatch();
  const { createLeadOrganisation } = useLeadOrganisationService();
  const { createLead } = useLeadContactService();

  const [form_data, setFormData] = useState(INITIAL_FORM);
  const [contacts, setContacts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const contact_key = useRef(0);

  const handleChange = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));
  const toggle = (name) => setFormData((prev) => ({ ...prev, [name]: !prev[name] }));

  const addContact = () =>
    setContacts((prev) => [...prev, { ...EMPTY_CONTACT, _key: contact_key.current++ }]);
  const removeContact = (index) => setContacts((prev) => prev.filter((_, i) => i !== index));
  const handleContactChange = (index, field, value) =>
    setContacts((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));

  const has_valid_contact = contacts?.some((c) => c.first_name.trim() !== '');
  const form_is_valid = form_data.name.trim() !== '' && has_valid_contact;

  const createContacts = async (organisation) => {
    const lead_id = organisation.id || organisation._id;
    const org_id = organisation.apollo_org_id || null;
    const valid_contacts = contacts?.filter((c) => c.first_name.trim());

    await Promise.all(
      valid_contacts?.map((c) =>
        createLead({
          lead_id,
          org_id,
          source: 'manual',
          first_name: c.first_name.trim(),
          last_name: c.last_name.trim(),
          title: c.title.trim(),
          email: c.email.trim() || null,
          phone: c.phone.trim() || null,
          has_email: Boolean(c.email.trim()),
          has_phone_number: Boolean(c.phone.trim())
        })
      )
    );
  };

  const handleSubmit = async () => {
    if (!form_is_valid) return;
    setSubmitting(true);

    const payload = {
      name: form_data.name.trim(),
      source: 'manual',
      website: form_data.website.trim() || null,
      industry: form_data.industry,
      headcount: toNumber(form_data.headcount),
      estimated_customers: toNumber(form_data.estimated_customers),
      score: toNumber(form_data.score) ?? 0,
      status: 'qualified',
      status_slug,
      has_web_app: form_data.has_web_app,
      has_mobile_app: form_data.has_mobile_app,
      has_newsletter: form_data.has_newsletter,
      qualified: true
    };

    const { organisation } = await createLeadOrganisation(payload);

    if (organisation && (organisation.id || organisation._id)) {
      await createContacts(organisation);
      dispatch(addManyLeadToStore([{ ...organisation, status_slug }]));
      setFormData(INITIAL_FORM);
      setContacts([]);
      closeModal();
    }

    setSubmitting(false);
  };

  return (
    <div className={styles.form}>
      <h2 className={styles.title}>Create Lead</h2>

      <Spacer multiple={3} />

      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Name" required>
            <SimpleInput
              value={form_data.name}
              onInput={(value) => handleChange('name', value)}
              placeholder="Acme Inc."
            />
          </DetailItem>
        </GridColumn>
      </GridRow>

      <Spacer multiple={2} />

      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Website">
            <SimpleInput
              value={form_data.website}
              onInput={(value) => handleChange('website', value)}
              placeholder="acme.com"
            />
          </DetailItem>
        </GridColumn>
      </GridRow>

      <Spacer multiple={2} />

      <GridRow num_of_columns={2}>
        <GridColumn>
          <DetailItem title="Industry">
            <MultiSelect
              options={INDUSTRY_OPTIONS}
              value={INDUSTRY_OPTIONS.find((option) => option.value === form_data.industry)}
              onChange={(option) => handleChange('industry', option.value)}
              menuPortalTarget={document.body}
              isMulti={false}
              isClearable={false}
              placeholder="Select industry"
            />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Headcount">
            <MultiSelect
              options={HEADCOUNT_OPTIONS}
              value={HEADCOUNT_OPTIONS.find((option) => option.value === form_data.headcount)}
              onChange={(option) => handleChange('headcount', option.value)}
              menuPortalTarget={document.body}
              isMulti={false}
              isClearable={false}
              sort_options_by="value"
              placeholder="Select size"
            />
          </DetailItem>
        </GridColumn>
      </GridRow>

      <Spacer multiple={2} />

      <GridRow num_of_columns={4}>
        <GridColumn span={3}>
          <DetailItem title="Estimated Customers">
            <MultiSelect
              options={ESTIMATED_CUSTOMERS_OPTIONS}
              value={ESTIMATED_CUSTOMERS_OPTIONS.find(
                (option) => option.value === form_data.estimated_customers
              )}
              onChange={(option) => handleChange('estimated_customers', option.value)}
              menuPortalTarget={document.body}
              isMulti={false}
              isClearable={false}
              sort_options_by="value"
              placeholder="Select range"
            />
          </DetailItem>
        </GridColumn>
        <GridColumn span={1}>
          <DetailItem title="Score">
            <SimpleInput
              type="number"
              min="0"
              value={form_data.score}
              onInput={(value) => handleChange('score', value)}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>

      <Spacer multiple={2} />

      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Product Signals">
            <div className={styles.checks}>
              <Checkbox
                label="Has web app"
                checked={form_data.has_web_app}
                onClick={() => toggle('has_web_app')}
              />
              <Checkbox
                label="Has mobile app"
                checked={form_data.has_mobile_app}
                onClick={() => toggle('has_mobile_app')}
              />
              <Checkbox
                label="Has newsletter"
                checked={form_data.has_newsletter}
                onClick={() => toggle('has_newsletter')}
              />
            </div>
          </DetailItem>
        </GridColumn>
      </GridRow>

      <Spacer multiple={2} />

      <GridRow num_of_columns={1}>
        <GridColumn>
          <div className={styles.contactsHeader}>
            <p className={styles.sectionLabel}>
              Contacts <span className={styles.requiredMark}>*</span>
            </p>
            <button type="button" className={styles.addContactBtn} onClick={addContact}>
              + Add contact
            </button>
          </div>

          {contacts?.length === 0 && (
            <p className={styles.contactsEmpty}>Add at least one contact to create this lead.</p>
          )}
          {contacts?.length > 0 && !has_valid_contact && (
            <p className={styles.contactsEmpty}>Each contact needs at least a first name.</p>
          )}

          {contacts?.map((contact, index) => (
            <div key={contact._key} className={styles.contactCard}>
              <div className={styles.contactCardHeader}>
                <span className={styles.contactCardTitle}>Contact {index + 1}</span>
                <button
                  type="button"
                  className={styles.removeContactBtn}
                  onClick={() => removeContact(index)}
                >
                  Remove
                </button>
              </div>

              <GridRow num_of_columns={2}>
                <GridColumn>
                  <DetailItem title="First name">
                    <SimpleInput
                      value={contact.first_name}
                      onInput={(value) => handleContactChange(index, 'first_name', value)}
                      placeholder="Jane"
                    />
                  </DetailItem>
                </GridColumn>
                <GridColumn>
                  <DetailItem title="Last name">
                    <SimpleInput
                      value={contact.last_name}
                      onInput={(value) => handleContactChange(index, 'last_name', value)}
                      placeholder="Doe"
                    />
                  </DetailItem>
                </GridColumn>
              </GridRow>

              <GridRow num_of_columns={1}>
                <GridColumn>
                  <DetailItem title="Title">
                    <SimpleInput
                      value={contact.title}
                      onInput={(value) => handleContactChange(index, 'title', value)}
                      placeholder="Head of Marketing"
                    />
                  </DetailItem>
                </GridColumn>
              </GridRow>

              <GridRow num_of_columns={2}>
                <GridColumn>
                  <DetailItem title="Email">
                    <SimpleInput
                      value={contact.email}
                      onInput={(value) => handleContactChange(index, 'email', value)}
                      placeholder="jane@acme.com"
                    />
                  </DetailItem>
                </GridColumn>
                <GridColumn>
                  <DetailItem title="Phone">
                    <SimpleInput
                      value={contact.phone}
                      onInput={(value) => handleContactChange(index, 'phone', value)}
                      placeholder="+1 555 000 0000"
                    />
                  </DetailItem>
                </GridColumn>
              </GridRow>
            </div>
          ))}
        </GridColumn>
      </GridRow>

      <Spacer multiple={3} />

      <div className={styles.actions}>
        <Button text="Cancel" type="secondary" onClick={closeModal} />
        <Button
          text="Create Lead"
          onClick={handleSubmit}
          disabled={!form_is_valid}
          loading={submitting}
        />
      </div>
    </div>
  );
};
